// ---------------------------------------------------------------------------
// A thin fetch wrapper around the Worker's `/api/*` REST surface.
// `VITE_API_BASE_URL` defaults to '' (same-origin), which is what production
// uses -- the SPA and the Worker share an origin (see docs/DEPLOY.md). The
// e2e harness (e2e/support/*) sets it at build time to point a built copy of
// this app at a locally-hosted worker instance on a different port.
// ---------------------------------------------------------------------------

import { createAuthClient } from 'better-auth/react';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

// ---------------------------------------------------------------------------
// Better Auth's own React client (`better-auth/react`, not a hand-written
// `fetch('/api/auth/...')`) -- it is a client-only bundle (no Kysely, no
// Node-only driver code; `apps/worker/src/auth/better-auth.ts`'s own comment
// explains why the Worker side needs the same care) and it does two things a
// hand-rolled wrapper would have to reimplement: a `useSession()` hook backed
// by a shared nanostore, so every component that reads the session (App's
// route gate, Shell's "signed in as", the sign-in screen) re-renders the
// moment a sign-in or sign-out resolves, with no manual refetch; and a
// dynamic path proxy (`authClient.signIn.email`, `authClient.signOut`) that
// maps 1:1 onto Better Auth's own route names, so this file never hand-codes
// `/api/auth/sign-in/email`'s request shape. `baseURL` reuses the same
// `BASE_URL` `api.get`/`api.post` use below (falsy in production, where it
// falls back to `window.location.origin` -- Better Auth's own default -- and
// the e2e harness's worker origin otherwise); its `basePath` default,
// `/api/auth`, already matches where `apps/worker/src/index.ts` mounts
// Better Auth's handler, so nothing further needs configuring here.
export const authClient = createAuthClient({ baseURL: BASE_URL || undefined });

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    // Every `/api/*` route past `requireSession` (apps/worker/src/auth/session.ts)
    // needs the Better Auth session cookie on the request -- `fetch`'s own
    // default credentials mode is `same-origin`, which silently drops that
    // cookie whenever the SPA and the Worker are on different origins (the
    // e2e harness always; production too, unless deployed to a shared
    // hostname -- see docs/DEPLOY.md). `authClient`'s own `$fetch`
    // (apps/web/src/api/client.ts's `createAuthClient` call) already
    // defaults to `include` for exactly this reason; this plain wrapper
    // needs the same override to match.
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...init?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null;
    throw new ApiError(res.status, body?.error ?? `Request to ${path} failed with status ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
};

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),
  post: <T>(path: string, body: unknown): Promise<T> => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  // `PATCH /api/projects/:id` (Task 3) is the only route this client patches
  // so far -- the brand-context screen's explicit Save changes (spec 19.1)
  // needs it, since a partial update is exactly what "leave every omitted
  // field untouched" (that route's own header comment) requires.
  patch: <T>(path: string, body: unknown): Promise<T> => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
};

// ---------------------------------------------------------------------------
// Wire types for the endpoints apps/web/src/routes/* consume. Kept next to
// the client rather than duplicated per route file; these mirror the JSON
// shapes apps/worker/src/api/{home,review,connections}.ts actually return.
// ---------------------------------------------------------------------------

export type NeedsReviewAttentionItem = {
  reason: 'needs_review';
  variantId: string;
  campaignId: string;
  projectId: string;
  projectName: string;
  campaignName: string | null;
  platform: 'x' | 'pinterest';
  connectionHandle: string | null;
  textExcerpt: string;
};

// `platform` reflects the connection this attention item is about, which is
// now looked up across every platform (Important 1 of the whole-branch
// review), not only X -- a Pinterest connection sitting in
// `renewal_required` produces a `reconnect-required` item with
// `platform: 'pinterest'` just as an X one would.
export type ReconnectRequiredAttentionItem = {
  reason: 'reconnect-required';
  projectId: string;
  projectName: string;
  platform: 'x' | 'pinterest';
  connectionHandle: string;
};

/**
 * A publish that was approved, scheduled, and then rejected by the platform.
 * `category` is the publisher's own `PublishErrorCategory` (packages/ports)
 * -- it decides which explanation the operator sees, because "out of credit"
 * and "the platform refused this content" need different next actions.
 */
export type PublicationFailedAttentionItem = {
  reason: 'publication_failed';
  variantId: string;
  campaignId: string;
  projectId: string;
  projectName: string;
  campaignName: string | null;
  platform: 'x' | 'pinterest';
  connectionHandle: string | null;
  textExcerpt: string;
  category: string | null;
  failedAt: string | null;
};

export type AttentionItem =
  | NeedsReviewAttentionItem
  | ReconnectRequiredAttentionItem
  | PublicationFailedAttentionItem;

// One entry per platform actually connected (a platform absent from the
// array is not connected) -- replaces a former single `xConnectionState`
// scalar, which could not represent an X connection and a Pinterest
// connection sitting in different states on the same project. Mirrors the
// shape `GET /api/projects/:id/connections` (`ConnectionRow`, below) already
// returns, minus `id`/`accountHandle`/`verifiedAt`, which Home's table does
// not need.
export type ProjectConnectionSummary = {
  platform: 'x' | 'pinterest';
  state: 'connected' | 'renewal_required' | 'revoked' | 'error';
};

export type ProjectRow = {
  id: string;
  name: string;
  website: string | null;
  automationMode: string;
  paused: boolean;
  connections: ProjectConnectionSummary[];
  attentionCount: number;
};

export type HomeResponse = { attention: AttentionItem[]; projects: ProjectRow[] };

export type ConnectionRow = {
  id: string;
  platform: 'x' | 'pinterest';
  accountHandle: string;
  state: 'connected' | 'renewal_required' | 'revoked' | 'error';
  verifiedAt: string | null;
};

export type MediaAttachmentDto = { assetId: string; altText: string; aiGenerated: boolean };

export type Board = { id: string; name: string };

// Mirrors `@asp/domain`'s `Content` union (XContent | PinterestContent) --
// apps/worker/src/api/review.ts returns `revision.content` unmodified from
// the domain layer, so this is the one place on the web side that shape has
// to be declared by hand (see this file's own module note on why: no
// compile-time dependency on @asp/domain crosses the worker boundary).
export type XContentDto = { kind: 'x'; text: string; media: MediaAttachmentDto[] };
export type PinterestContentDto = {
  kind: 'pinterest';
  boardId: string;
  boardSectionId: string | null;
  title: string;
  description: string;
  link: string | null;
  image: MediaAttachmentDto;
};
export type ContentDto = XContentDto | PinterestContentDto;

export type VariantDetail = {
  variant: {
    id: string; state: string; campaignId: string; projectId: string;
    connectionId: string | null; currentRevisionId: string; approvedRevisionId: string | null;
  };
  project: { id: string; name: string; website: string | null };
  campaign: { id: string; name: string };
  connection: { id: string; platform: 'x' | 'pinterest'; accountHandle: string; state: string } | null;
  revision: {
    id: string; number: number;
    content: ContentDto;
    createdAt: string; origin: 'ui' | 'mcp'; originClient: string | null;
  };
  validation: { issues: Array<{ field: string; index?: number; code: string; message: string; current: number | string; limit?: number }> };
  attempts: Array<{ attempt: number; category: string | null; message: string | null; ok: boolean; at: string }>;
  schedule: { id: string; publishAtUtc: string; state: string } | null;
  publication: { id: string; providerId: string; publicUrl: string } | null;
  activity: Array<{ id: string; origin: string; actor: string; action: string; result: string; at: string }>;
};

// Mirrors `Project` (packages/ports/src/repositories.ts) as returned by
// `POST /api/projects`, `GET /api/projects/:id`, and `PATCH /api/projects/:id`
// (apps/worker/src/index.ts) -- all three hand back the same row shape, so
// one type serves all three rather than three that could drift apart.
//
// Task 4 fix: this used to declare a single `brandContext: string | null`
// field, which no route on this branch has ever sent (Task 3 replaced that
// former single-blob column with the five named fields below, on the
// server -- packages/ports/src/repositories.ts's own comment on `Project`
// explains why). Nothing read `brandContext` before this fix, so the drift
// was silent; it is fixed here rather than left for the next reader to
// discover as a field that renders `undefined`.
export type ProjectSummary = {
  id: string; workspaceId: string; name: string; website: string | null;
  productSummary: string | null; targetAudience: string | null; brandVoice: string | null;
  aiInstructions: string | null; expressionsToAvoid: string | null;
  automationMode: string; dailyCap: number; paused: boolean;
};

export const fetchHome = (): Promise<HomeResponse> => api.get('/api/home');

// Whole-branch review I3: the only thing missing was a caller for the
// already-existing `POST /api/projects` route (apps/worker/src/index.ts) --
// the route itself required no changes.
export const createProject = (input: { name: string; website: string | null; dailyCap: number }): Promise<{ project: ProjectSummary }> =>
  api.post('/api/projects', input);

// `GET /api/projects/:id` -- the read path Task 4 added because the brand
// context screen (spec 13.6) needs to load an *existing* project's five
// brand-context fields on its own, not only read them back from the
// response to a write it just made. 404s (never 403) for a project in
// another workspace, same as every other project-scoped route.
export const fetchProject = (projectId: string): Promise<{ project: ProjectSummary }> => api.get(`/api/projects/${projectId}`);

// A partial update for `PATCH /api/projects/:id` -- mirrors `ProjectUpdate`
// (packages/ports/src/repositories.ts) field for field. Every field is
// optional and the six brand-context-adjacent ones are additionally
// nullable, matching that route's own omitted-vs-null contract: a key
// absent from the JSON body leaves the field untouched server-side, and an
// explicit `null` clears it.
export type ProjectPatch = {
  name?: string;
  website?: string | null;
  productSummary?: string | null;
  targetAudience?: string | null;
  brandVoice?: string | null;
  aiInstructions?: string | null;
  expressionsToAvoid?: string | null;
  dailyCap?: number;
  paused?: boolean;
};

export const updateProject = (projectId: string, patch: ProjectPatch): Promise<{ project: ProjectSummary }> =>
  api.patch(`/api/projects/${projectId}`, patch);

export const fetchProjectConnections = (projectId: string): Promise<{ connections: ConnectionRow[] }> =>
  api.get(`/api/projects/${projectId}/connections`);

// apps/worker/src/api/boards.ts 404s a project with no Pinterest connection
// yet, and otherwise makes a live call to Pinterest on every request (no
// caching) -- so a non-404 failure here means a connection exists but the
// live lookup itself failed, not that the project is disconnected.
export const fetchProjectBoards = (projectId: string): Promise<{ boards: Board[] }> =>
  api.get(`/api/projects/${projectId}/boards`);

export const fetchVariant = (variantId: string): Promise<VariantDetail> => api.get(`/api/variants/${variantId}`);

export const approveVariant = (variantId: string, revisionId: string): Promise<{ state: string }> =>
  api.post(`/api/variants/${variantId}/approve`, { revisionId });

export const requestChanges = (variantId: string, reason: string): Promise<{ state: string }> =>
  api.post(`/api/variants/${variantId}/request-changes`, { reason });

export const scheduleVariant = (variantId: string, publishAt: string): Promise<{ scheduleId: string; publishAtUtc: string }> =>
  api.post(`/api/variants/${variantId}/schedule`, { publishAt });

export const cancelSchedule = (variantId: string): Promise<{ state: string }> =>
  api.post(`/api/variants/${variantId}/cancel-schedule`, {});

// ---------------------------------------------------------------------------
// `GET /api/projects` (apps/worker/src/index.ts), the extended shape Task 3
// added for the Projects list screen (UI structure spec 13.1) -- distinct
// from `ProjectRow` above (Home's own, narrower shape) because the two
// routes return different fields (`dailyCap`, `nextPublishAtUtc` are only
// here) and this file has no compile-time link to the Worker to catch a
// drift between them.
// ---------------------------------------------------------------------------

export type ProjectListRow = {
  id: string;
  name: string;
  website: string | null;
  automationMode: string;
  paused: boolean;
  dailyCap: number;
  connections: ProjectConnectionSummary[];
  nextPublishAtUtc: string | null;
  attentionCount: number;
};

export type ProjectsResponse = { projects: ProjectListRow[] };

export const fetchProjects = (): Promise<ProjectsResponse> => api.get('/api/projects');

// ---------------------------------------------------------------------------
// `GET /api/campaigns` (apps/worker/src/api/campaigns.ts), the Campaigns
// list screen's source (UI structure spec 10.1). Every variant carries its
// own `state` -- the field the summary line on the campaigns screen may
// never replace, per that section's own rule.
// ---------------------------------------------------------------------------

export type CampaignVariantRow = {
  id: string;
  platform: 'x' | 'pinterest';
  state: string;
  publishAtUtc: string | null;
};

export type CampaignRow = {
  id: string;
  name: string;
  objective: string | null;
  projectId: string;
  projectName: string;
  variants: CampaignVariantRow[];
  nextPublishAtUtc: string | null;
};

export type CampaignsResponse = { campaigns: CampaignRow[] };

export const fetchCampaigns = (): Promise<CampaignsResponse> => api.get('/api/campaigns');

// ---------------------------------------------------------------------------
// `GET /api/activity` (apps/worker/src/api/activity.ts), the Activity
// screen's source (UI structure spec 15). `actor` is whatever the Worker
// already resolved it to (the MCP client label when the grant carries one,
// else the acting id -- see that file's own header comment); this file
// never invents a client identity that was not on the wire.
// ---------------------------------------------------------------------------

export type ActivityOrigin = 'ui' | 'mcp' | 'scheduler' | 'publisher' | 'system';
export type ActivityResult = 'ok' | 'failed';

export type ActivityEvent = {
  id: string;
  at: string;
  projectId: string | null;
  projectName: string | null;
  origin: ActivityOrigin;
  actor: string;
  action: string;
  target: string;
  result: ActivityResult;
  metadata: Record<string, unknown>;
};

export type ActivityResponse = { events: ActivityEvent[] };

export type ActivityFilter = {
  projectId?: string;
  origin?: ActivityOrigin;
  target?: string;
  result?: ActivityResult;
  since?: string;
  until?: string;
  limit?: number;
};

export const fetchActivity = (filter?: ActivityFilter): Promise<ActivityResponse> => {
  const params = new URLSearchParams();
  if (filter?.projectId) params.set('projectId', filter.projectId);
  if (filter?.origin) params.set('origin', filter.origin);
  if (filter?.target) params.set('target', filter.target);
  if (filter?.result) params.set('result', filter.result);
  if (filter?.since) params.set('since', filter.since);
  if (filter?.until) params.set('until', filter.until);
  if (filter?.limit !== undefined) params.set('limit', String(filter.limit));
  const qs = params.toString();
  return api.get(`/api/activity${qs ? `?${qs}` : ''}`);
};

// ---------------------------------------------------------------------------
// `GET /api/mcp-grants` / `POST /api/mcp-grants/:id/revoke`
// (apps/worker/src/mcp/grants.ts), the AI connections screen's source (UI
// structure spec 16). Mirrors `toWireGrant`'s shape there field for field:
// no `token` field exists on this type because the Worker never sends one
// past the one-time mint response (`POST /api/mcp-grants`, which this
// screen does not call -- minting is not part of this phase's UI, per the
// task-4 brief). `writeScopes` is computed server-side from `MCP_WRITE_SCOPES`
// (apps/worker/src/mcp/auth.ts) -- this file has no compile-time link to
// that constant, so it renders exactly the array the wire sends rather than
// recomputing which scopes count as "write" itself.
// ---------------------------------------------------------------------------

export type McpGrantState = 'active' | 'revoked';

export type McpScope =
  | 'projects:read' | 'projects:write' | 'campaigns:read' | 'campaigns:write' | 'media:write'
  | 'schedule:write' | 'approval:write' | 'publish:write' | 'automation:write' | 'activity:read';

export type McpGrant = {
  id: string;
  client: string;
  scopes: McpScope[];
  state: McpGrantState;
  authorizedAt: string;
  lastActivityAt: string | null;
  writeScopes: McpScope[];
};

export type McpGrantsResponse = { endpoint: string; grants: McpGrant[] };

export const fetchMcpGrants = (): Promise<McpGrantsResponse> => api.get('/api/mcp-grants');

export const revokeMcpGrant = (id: string): Promise<{ grant: McpGrant }> =>
  api.post(`/api/mcp-grants/${id}/revoke`, {});

// ---------------------------------------------------------------------------
// `GET /api/calendar` (apps/worker/src/api/calendar.ts), the Calendar
// screen's source (UI structure spec 9). Mirrors that route's own mapped
// entry shape field for field, checked directly against the handler rather
// than assumed:
//   scheduleId, variantId, campaignId, campaignName, projectId, projectName,
//   platform, publishAtUtc, scheduleState, variantState, approvalRequired,
//   textExcerpt
// `scheduleState` and `variantState` are declared as plain `string` rather
// than a narrowed literal union -- the same choice `VariantDetail.variant.state`
// and `VariantDetail.schedule.state` already make above, for the same reason
// this file's own header comment gives: no compile-time link to the Worker,
// so a value this file has never seen (a future schedule or variant state)
// still renders instead of failing to type-check against a union that has
// gone stale.
//
// Only `from`/`to` are sent by this screen's own `fetchCalendar` below --
// the route also accepts `projectId`/`platform`/`state` query filters, but
// this screen fetches the whole range unfiltered and applies project,
// platform, and schedule-state filters client-side (see
// routes/calendar.tsx's own `filterEntries`). That is a deliberate choice,
// not an oversight: spec 9.6 requires the calendar to distinguish "nothing
// scheduled in this range" from "something is scheduled, but the active
// filters hide it," and the only way to tell those apart from a single
// request is to see the unfiltered range and filter it in the browser.
// ---------------------------------------------------------------------------

export type CalendarEntry = {
  scheduleId: string;
  variantId: string;
  campaignId: string;
  campaignName: string;
  projectId: string;
  projectName: string;
  platform: 'x' | 'pinterest';
  publishAtUtc: string;
  scheduleState: string;
  variantState: string;
  approvalRequired: boolean;
  textExcerpt: string;
};

export type CalendarResponse = { entries: CalendarEntry[] };

export const fetchCalendar = (range: { from: string; to: string }): Promise<CalendarResponse> => {
  const params = new URLSearchParams({ from: range.from, to: range.to });
  return api.get(`/api/calendar?${params.toString()}`);
};
